/**
 * ============================================
 * DASHBOARD - Clean Modern Home Screen
 * ============================================
 * 
 * DATA SOURCES:
 * - DataContext: Provides jobs, estimates, customers, events, inventory
 *   - Jobs: /companies/{companyId}/jobs/{jobId}
 *     - Fields: jobId, customerName, status, total, paymentStatus, createdAt, updatedAt, workItems[]
 *     - workItems have: scheduledDate (for today's schedule)
 *   - Estimates: /companies/{companyId}/estimates/{estimateId}
 *     - Fields: estimateId, customerName, status ('Created', 'Sent', 'Approved', etc.), total, createdAt, updatedAt
 *   - Customers: /companies/{companyId}/customers/{customerId}
 *   - Events: /companies/{companyId}/events/{eventId}
 *   - Inventory: /companies/{companyId}/inventory/{itemId}
 * 
 * - CompanyMembershipContext: Provides companyId for data scoping
 * 
 * NOTE: For this version, NO role filtering is implemented.
 * All users see the same dashboard regardless of role.
 * TODO: Add role-based visibility later if needed.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Skeleton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { useData } from '../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Work as WorkIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Event as EventIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
  PersonAdd as PersonAddIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

export default function Dashboard() {
  const { estimates = [], jobs = [], customers = [], loading } = useData();
  const navigate = useNavigate();
  const now = moment();

  // ============================================
  // DATE HELPERS
  // ============================================
  const isToday = (date) => moment(date).isSame(now, 'day');
  const isThisMonth = (date) => moment(date).isSame(now, 'month');
  const isLastMonth = (date) => moment(date).isSame(moment(now).subtract(1, 'month'), 'month');
  const isLast7Days = (date) => moment(date).isAfter(moment(now).subtract(7, 'days'));

  // ============================================
  // KPI CALCULATIONS
  // ============================================

  // 1. Jobs Today - Count jobs with work items scheduled for today
  const jobsTodayCount = useMemo(() => {
    // Also check if jobs themselves have a scheduledDate or date field for today
    const jobsWithTodayDate = jobs.filter(job => {
      if (job.scheduledDate && isToday(job.scheduledDate)) return true;
      if (job.date && isToday(job.date)) return true;
      return false;
    });

    const workItemsToday = jobs.flatMap(job =>
      (job.workItems || [])
        .filter(work => work.scheduledDate && isToday(work.scheduledDate))
        .map(work => ({ ...work, jobId: job.id, job: job }))
    );
    
    // Group by job ID to count unique jobs (from work items)
    const uniqueJobsFromWorkItems = new Set(workItemsToday.map(w => w.jobId));
    
    // Combine both: jobs scheduled for today directly + jobs with work items today
    const allTodayJobs = new Set([
      ...Array.from(uniqueJobsFromWorkItems),
      ...jobsWithTodayDate.map(j => j.id)
    ]);
    
    return allTodayJobs.size;
  }, [jobs]);

  // 2. Pending Estimates - Estimates that are "Created" or "Sent" (not approved/rejected)
  const pendingEstimates = useMemo(() => {
    return estimates.filter(e =>
      e.status === 'Created' ||
      e.status === 'Sent' ||
      e.status === 'pending' ||
      e.status === 'Pending'
    ).length;
  }, [estimates]);

  // 3. Unpaid Invoices - Jobs that are not fully paid
  const unpaidInvoices = useMemo(() => {
    return jobs.filter(j =>
      j.paymentStatus !== 'Paid' &&
      j.paymentStatus !== 'paid' &&
      (j.paymentStatus === 'Unpaid' ||
       j.paymentStatus === 'unpaid' ||
       j.paymentStatus === 'Partially Paid' ||
       j.paymentStatus === 'pending' ||
       !j.paymentStatus)
    );
  }, [jobs]);

  const unpaidInvoicesCount = unpaidInvoices.length;
  const unpaidInvoicesTotal = unpaidInvoices.reduce((sum, job) => {
    const remainingBalance = job.remainingBalance !== undefined
      ? job.remainingBalance
      : (job.total || 0) - (job.totalPaid || 0);
    return sum + Math.max(0, remainingBalance);
  }, 0);

  // 4. Revenue This Month - Sum of paid invoices created/paid this month
  const revenueThisMonth = useMemo(() => {
    return jobs
      .filter(j =>
        (j.paymentStatus === 'Paid' || j.paymentStatus === 'paid') &&
        ((j.paidAt && isThisMonth(j.paidAt)) ||
         (j.createdAt && isThisMonth(j.createdAt)))
      )
      .reduce((sum, j) => sum + (j.total || 0), 0);
  }, [jobs]);

  // ============================================
  // TODAY'S SCHEDULE
  // ============================================
  const todaysSchedule = useMemo(() => {
    const workItemsToday = jobs.flatMap(job =>
      (job.workItems || [])
        .filter(work => work.scheduledDate && isToday(work.scheduledDate))
        .map(work => ({
          ...work,
          jobId: job.id,
          job: job,
          time: moment(work.scheduledDate).format('HH:mm'),
        }))
    );

    // Sort by time
    workItemsToday.sort((a, b) => a.time.localeCompare(b.time));

    // Group by job
    const grouped = {};
    workItemsToday.forEach(work => {
      if (!grouped[work.jobId]) {
        grouped[work.jobId] = {
          job: work.job,
          workItems: [],
        };
      }
      grouped[work.jobId].workItems.push(work);
    });

    return Object.values(grouped);
  }, [jobs]);

  // ============================================
  // RECENT ACTIVITY FEED
  // ============================================
  const recentActivity = useMemo(() => {
    const activities = [];

    // Job activities
    jobs.forEach(job => {
      if (job.createdAt && isLast7Days(job.createdAt)) {
        activities.push({
          type: 'job_created',
          timestamp: moment(job.createdAt),
          title: `Job #${job.jobId || job.id} created`,
          subtitle: job.customerName || 'Customer',
          amount: job.total,
          link: `/jobs/${job.id}`,
        });
      }

      if (job.updatedAt && isLast7Days(job.updatedAt)) {
        if (job.status === 'Completed') {
          activities.push({
            type: 'job_completed',
            timestamp: moment(job.updatedAt),
            title: `Job #${job.jobId || job.id} completed`,
            subtitle: job.customerName || 'Customer',
            amount: job.total,
            link: `/jobs/${job.id}`,
          });
        }
      }

      if (job.paidAt && isLast7Days(job.paidAt)) {
        activities.push({
          type: 'invoice_paid',
          timestamp: moment(job.paidAt),
          title: `Invoice #${job.jobId || job.id} paid`,
          subtitle: job.customerName || 'Customer',
          amount: job.total,
          link: `/jobs/${job.id}`,
        });
      }

      if (job.createdAt && job.paymentStatus === 'Paid' && isLast7Days(job.createdAt)) {
        // Only if it wasn't already added via paidAt
        if (!job.paidAt || !isLast7Days(job.paidAt)) {
          activities.push({
            type: 'invoice_paid',
            timestamp: moment(job.createdAt),
            title: `Invoice #${job.jobId || job.id} paid`,
            subtitle: job.customerName || 'Customer',
            amount: job.total,
            link: `/jobs/${job.id}`,
          });
        }
      }
    });

    // Estimate activities
    estimates.forEach(estimate => {
      if (estimate.createdAt && isLast7Days(estimate.createdAt)) {
        activities.push({
          type: 'estimate_created',
          timestamp: moment(estimate.createdAt),
          title: `Estimate #${estimate.estimateId || estimate.id} created`,
          subtitle: estimate.customerName || 'Customer',
          amount: estimate.total,
          link: `/estimates/${estimate.id}`,
        });
      }

      if (estimate.updatedAt && isLast7Days(estimate.updatedAt)) {
        if (estimate.status === 'Approved') {
          activities.push({
            type: 'estimate_approved',
            timestamp: moment(estimate.updatedAt),
            title: `Estimate #${estimate.estimateId || estimate.id} approved`,
            subtitle: estimate.customerName || 'Customer',
            amount: estimate.total,
            link: `/estimates/${estimate.id}`,
          });
        }
      }
    });

    // Sort by timestamp (newest first)
    activities.sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf());

    // Return top 15
    return activities.slice(0, 15);
  }, [jobs, estimates]);

  // ============================================
  // FINANCE SNAPSHOT
  // ============================================
  const financeSnapshot = useMemo(() => {
    const paidThisMonth = jobs
      .filter(j =>
        (j.paymentStatus === 'Paid' || j.paymentStatus === 'paid') &&
        ((j.paidAt && isThisMonth(j.paidAt)) ||
         (j.createdAt && isThisMonth(j.createdAt)))
      )
      .reduce((sum, j) => sum + (j.total || 0), 0);

    const outstanding = unpaidInvoicesTotal;

    const paidLastMonth = jobs
      .filter(j =>
        (j.paymentStatus === 'Paid' || j.paymentStatus === 'paid') &&
        ((j.paidAt && isLastMonth(j.paidAt)) ||
         (j.createdAt && isLastMonth(j.createdAt)))
      )
      .reduce((sum, j) => sum + (j.total || 0), 0);

    return { paidThisMonth, outstanding, paidLastMonth };
  }, [jobs, unpaidInvoicesTotal]);

  // ============================================
  // KPI CARD COMPONENT
  // ============================================
  const KPICard = ({ title, value, subtitle, icon, color = 'primary', onClick, loading: cardLoading }) => (
    <Paper
      onClick={onClick}
      sx={{
        p: { xs: 2, sm: 3 },
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {cardLoading ? (
        <>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: `${color}.main` }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ color: `${color}.main` }}>
              {icon}
            </Box>
          </Box>
          {onClick && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1 }}>
              <Typography variant="caption" color="primary" sx={{ mr: 0.5 }}>
                View
              </Typography>
              <ArrowForwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </Box>
          )}
        </>
      )}
    </Paper>
  );

  // ============================================
  // ACTIVITY ITEM COMPONENT
  // ============================================
  const ActivityItem = ({ activity }) => {
    const getIcon = () => {
      switch (activity.type) {
        case 'job_created':
          return <WorkIcon color="primary" />;
        case 'job_completed':
          return <CheckCircleIcon color="success" />;
        case 'estimate_created':
          return <DescriptionIcon color="info" />;
        case 'estimate_approved':
          return <CheckCircleIcon color="success" />;
        case 'invoice_paid':
          return <AttachMoneyIcon color="success" />;
        default:
          return <EventIcon />;
      }
    };

    const getTimeAgo = () => {
      return activity.timestamp.fromNow();
    };

    return (
      <ListItem
        button
        onClick={() => activity.link && navigate(activity.link)}
        sx={{
          borderLeft: `3px solid`,
          borderColor: activity.type.includes('paid') || activity.type.includes('approved') || activity.type.includes('completed')
            ? 'success.main'
            : 'primary.main',
          mb: 0.5,
          borderRadius: '4px',
        }}
      >
        <ListItemIcon>{getIcon()}</ListItemIcon>
        <ListItemText
          primary={activity.title}
          secondary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {activity.subtitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {activity.amount && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                    ${activity.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {getTimeAgo()}
                </Typography>
              </Box>
            </Box>
          }
        />
      </ListItem>
    );
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width="30%" height={60} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }, mb: 3 }}
      >
        Dashboard
      </Typography>

      {/* TOP: 4 KPI CARDS */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <KPICard
            title="Jobs Today"
            value={jobsTodayCount}
            subtitle={`${todaysSchedule.length} job${todaysSchedule.length !== 1 ? 's' : ''} scheduled`}
            icon={<WorkIcon sx={{ fontSize: 32 }} />}
            color="primary"
            onClick={() => navigate('/schedule')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KPICard
            title="Pending Estimates"
            value={pendingEstimates}
            subtitle="Awaiting approval"
            icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
            color="warning"
            onClick={() => navigate('/estimates')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KPICard
            title="Unpaid Invoices"
            value={unpaidInvoicesCount}
            subtitle={`$${unpaidInvoicesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} outstanding`}
            icon={<ReceiptIcon sx={{ fontSize: 32 }} />}
            color="error"
            onClick={() => navigate('/jobs')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KPICard
            title="Revenue This Month"
            value={`$${revenueThisMonth.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            subtitle={now.format('MMMM YYYY')}
            icon={<AttachMoneyIcon sx={{ fontSize: 32 }} />}
            color="success"
            onClick={() => navigate('/analytics')}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* LEFT COLUMN */}
        <Grid item xs={12} lg={8}>
          {/* TODAY'S SCHEDULE */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Today's Schedule
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/schedule')}
              >
                View Full Calendar
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {todaysSchedule.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">
                  No jobs scheduled for today
                </Typography>
              </Box>
            ) : (
              <List>
                {todaysSchedule.map(({ job, workItems }) => {
                  // Sort work items by time
                  const sortedItems = [...workItems].sort((a, b) => a.time.localeCompare(b.time));
                  const firstTime = moment(sortedItems[0].scheduledDate).format('h:mm A');
                  const lastTime = sortedItems.length > 1
                    ? moment(sortedItems[sortedItems.length - 1].scheduledDate).format('h:mm A')
                    : null;

                  return (
                    <ListItem
                      key={job.id}
                      button
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      sx={{
                        mb: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {job.customerName || 'Customer'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Job #{job.jobId || job.id}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {firstTime}{lastTime && lastTime !== firstTime ? ` - ${lastTime}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • {sortedItems.length} item{sortedItems.length !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Box>
                        {job.address && (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: '40%' }}>
                            {job.address}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>

          {/* RECENT ACTIVITY FEED */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {recentActivity.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">
                  No recent activity
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                {recentActivity.map((activity, idx) => (
                  <ActivityItem key={idx} activity={activity} />
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} lg={4}>
          {/* FINANCE SNAPSHOT */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Finance Snapshot
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Paid This Month
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  ${financeSnapshot.paidThisMonth.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                {financeSnapshot.paidLastMonth > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Last month: ${financeSnapshot.paidLastMonth.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Outstanding
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  ${financeSnapshot.outstanding.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {unpaidInvoicesCount} unpaid invoice{unpaidInvoicesCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => navigate('/analytics')}
              endIcon={<ArrowForwardIcon />}
            >
              View Full Analytics
            </Button>
          </Paper>

          {/* QUICK ACTIONS */}
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate('/estimates/new')}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                Create Estimate
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<WorkIcon />}
                onClick={() => navigate('/jobs/new')}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                Create Job
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate('/customers/new')}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                Add Customer
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => navigate('/schedule')}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                Schedule Appointment
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PaymentIcon />}
                onClick={() => navigate('/jobs')}
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                Add Payment
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
